import * as crypto from "crypto";
import * as fs from "fs/promises";
import * as path from "path";
import type {
  StorageFile,
  StorageListInput,
  StorageListPage,
  StorageStoreOptions,
  StorageVisibility,
  StoredFile,
  UploadGrantInput,
} from "@gencow/core";
import { DEFAULT_STORAGE_QUOTA, MAX_FILE_SIZE, StorageListUnsupportedError, formatBytes } from "@gencow/core";
import { createSystemFilesCatalog } from "./storage-system-files-catalog.js";
import { createStorageReadGrant } from "./storage-read-grant.js";
import { normalizeStorageVisibility } from "./storage-visibility.js";
import type {
  LocalStorageFile,
  RuntimeStorageOptions,
  StorageOptions,
  StorageRuntimeAdapter,
  SystemFilesCatalog,
} from "./storage-types.js";

const metaStore = new Map<string, LocalStorageFile>();
const DIRECT_UPLOAD_TTL_MS = 15 * 60 * 1000;

type PendingUploadMeta = {
  filename: string;
  contentType: string;
  declaredSize: number;
  checksum?: string;
  expiresAt: string;
};

function normalizeChecksum(checksum: string | undefined): string | undefined {
  if (!checksum) return undefined;
  const normalized = checksum.trim().toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(normalized)) throw new Error("Invalid upload checksum");
  return normalized;
}

async function loadStorageMeta(params: {
  storageId: string;
  dir: string;
  metaStore: Map<string, LocalStorageFile>;
}): Promise<LocalStorageFile | null> {
  const cached = params.metaStore.get(params.storageId);
  if (cached) return cached;

  const filePath = path.join(params.dir, params.storageId);
  try {
    const raw = await fs.readFile(`${filePath}.meta`, "utf-8");
    const parsed = JSON.parse(raw) as {
      name?: unknown;
      type?: unknown;
      size?: unknown;
      visibility?: unknown;
    };
    const stat = await fs.stat(filePath);
    const meta: LocalStorageFile = {
      id: params.storageId,
      name: typeof parsed.name === "string" ? parsed.name : params.storageId,
      size: typeof parsed.size === "number" ? parsed.size : stat.size,
      type: typeof parsed.type === "string" ? parsed.type : "application/octet-stream",
      visibility: normalizeStorageVisibility(parsed.visibility),
      path: filePath,
    };
    params.metaStore.set(params.storageId, meta);
    return meta;
  } catch {
    return null;
  }
}

async function assertFileSize(size: number): Promise<void> {
  if (size <= MAX_FILE_SIZE) return;
  throw new Error(`File too large: ${formatBytes(size)} exceeds limit of ${formatBytes(MAX_FILE_SIZE)}`);
}

async function removeCachedTransforms(dir: string, storageId: string): Promise<void> {
  const cacheDir = path.join(dir, ".cache");
  try {
    const entries = await fs.readdir(cacheDir);
    const prefix = `${storageId}_`;
    await Promise.all(
      entries
        .filter((entry) => entry.startsWith(prefix))
        .map((entry) => fs.unlink(path.join(cacheDir, entry)).catch(() => {})),
    );
  } catch {
    /* .cache 디렉토리 미존재 시 무시 */
  }
}

async function removeStoredObjectFiles(params: {
  dir: string;
  filePath: string;
  storageId: string;
  metaStore: Map<string, LocalStorageFile>;
}): Promise<void> {
  await fs.unlink(params.filePath).catch(() => {});
  await fs.unlink(`${params.filePath}.meta`).catch(() => {});
  params.metaStore.delete(params.storageId);
  await removeCachedTransforms(params.dir, params.storageId);
}

async function ensureStorageDir(dir: string): Promise<void> {
  await fs.mkdir(dir, { recursive: true });
}

function uploadDir(dir: string): string {
  return path.join(dir, ".uploads");
}

function normalizeUploadInput(input: UploadGrantInput): PendingUploadMeta {
  if (!input.filename || input.filename.length > 500) throw new Error("Invalid upload filename");
  if (!input.contentType || input.contentType.length > 200) throw new Error("Invalid upload content type");
  if (!Number.isFinite(input.size) || input.size < 0) throw new Error("Invalid upload size");
  if (input.size > MAX_FILE_SIZE) {
    throw new Error(
      `File too large: ${formatBytes(input.size)} exceeds limit of ${formatBytes(MAX_FILE_SIZE)}`,
    );
  }
  return {
    filename: input.filename,
    contentType: input.contentType,
    declaredSize: input.size,
    ...(input.checksum ? { checksum: normalizeChecksum(input.checksum) } : {}),
    expiresAt: new Date(Date.now() + DIRECT_UPLOAD_TTL_MS).toISOString(),
  };
}

function normalizeStoreOptions(input: string | StorageStoreOptions | undefined): {
  filename?: string;
  visibility: StorageVisibility;
} {
  if (typeof input === "string") return { filename: input, visibility: "private" };
  return {
    filename: input?.filename,
    visibility: normalizeStorageVisibility(input?.visibility),
  };
}

function encodeMeta(meta: Pick<StorageFile, "name" | "type" | "size" | "visibility">): string {
  return JSON.stringify({
    name: meta.name,
    type: meta.type,
    size: meta.size,
    visibility: normalizeStorageVisibility(meta.visibility),
  });
}

async function readPendingUploadMeta(dir: string, uploadId: string): Promise<PendingUploadMeta> {
  const raw = await fs.readFile(path.join(uploadDir(dir), `${uploadId}.json`), "utf-8");
  return JSON.parse(raw) as PendingUploadMeta;
}

async function removePendingUpload(dir: string, uploadId: string): Promise<void> {
  await fs.unlink(path.join(uploadDir(dir), uploadId)).catch(() => {});
  await fs.unlink(path.join(uploadDir(dir), `${uploadId}.json`)).catch(() => {});
}

function sha256(buffer: Buffer): string {
  return crypto.createHash("sha256").update(buffer).digest("hex");
}

function toPublicStorageFile(meta: LocalStorageFile): StorageFile {
  return {
    id: meta.id,
    name: meta.name,
    size: meta.size,
    type: meta.type,
    visibility: normalizeStorageVisibility(meta.visibility),
  };
}

async function recordStored(params: {
  catalog?: SystemFilesCatalog;
  meta: StorageFile;
  uploadedBy: string;
}): Promise<void> {
  if (!params.catalog) return;
  try {
    await params.catalog.recordStored({ ...params.meta, uploadedBy: params.uploadedBy });
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    console.warn(`[storage] DB record failed for ${params.meta.id}: ${msg}`);
    throw new Error("Storage catalog record failed");
  }
}

export function createLocalFilesystemStorage(
  dir: string = "./uploads",
  options?: StorageOptions & { catalog?: SystemFilesCatalog },
): StorageRuntimeAdapter {
  const catalog =
    options?.catalog ?? (options?.rawSql ? createSystemFilesCatalog(options.rawSql) : undefined);
  const quota = options?.storageQuota ?? DEFAULT_STORAGE_QUOTA;

  return {
    kind: "local-filesystem",

    async store(file: File | Blob, filenameOrOptions?: string | StorageStoreOptions): Promise<string> {
      await assertFileSize(file.size);
      await catalog?.assertCanStore({ size: file.size, quotaBytes: quota });
      await ensureStorageDir(dir);

      const storeOptions = normalizeStoreOptions(filenameOrOptions);
      const id = crypto.randomUUID();
      const name = storeOptions.filename || (file instanceof File ? file.name : `${id}.bin`);
      const type = file.type || "application/octet-stream";
      const filePath = path.join(dir, id);
      const arrayBuffer = await file.arrayBuffer();

      await fs.writeFile(filePath, Buffer.from(arrayBuffer));
      await fs
        .writeFile(
          `${filePath}.meta`,
          encodeMeta({ name, type, size: file.size, visibility: storeOptions.visibility }),
        )
        .catch(() => {});

      const meta: LocalStorageFile = {
        id,
        name,
        size: file.size,
        type,
        visibility: storeOptions.visibility,
        path: filePath,
      };
      metaStore.set(id, meta);
      try {
        await recordStored({ catalog, meta: toPublicStorageFile(meta), uploadedBy: "api" });
      } catch (error) {
        await removeStoredObjectFiles({ dir, filePath, storageId: id, metaStore });
        throw error;
      }
      return id;
    },

    async storeBuffer(
      buffer: Buffer,
      filename: string,
      type: string = "application/octet-stream",
      options?: { visibility?: StorageVisibility },
    ): Promise<string> {
      await assertFileSize(buffer.length);
      await catalog?.assertCanStore({ size: buffer.length, quotaBytes: quota });
      await ensureStorageDir(dir);

      const id = crypto.randomUUID();
      const visibility = normalizeStorageVisibility(options?.visibility);
      const filePath = path.join(dir, id);
      await fs.writeFile(filePath, buffer);
      await fs
        .writeFile(`${filePath}.meta`, encodeMeta({ name: filename, type, size: buffer.length, visibility }))
        .catch(() => {});

      const meta: LocalStorageFile = {
        id,
        name: filename,
        size: buffer.length,
        type,
        visibility,
        path: filePath,
      };
      metaStore.set(id, meta);
      try {
        await recordStored({ catalog, meta: toPublicStorageFile(meta), uploadedBy: "api" });
      } catch (error) {
        await removeStoredObjectFiles({ dir, filePath, storageId: id, metaStore });
        throw error;
      }
      return id;
    },

    getUrl(storageId: string): string {
      return `/api/storage/${storageId}`;
    },

    getPublicUrl(storageId: string): string {
      return `/api/storage/${storageId}`;
    },

    async createUploadGrant(input) {
      const meta = normalizeUploadInput(input);
      await catalog?.assertCanStore({ size: meta.declaredSize, quotaBytes: quota });
      await fs.mkdir(uploadDir(dir), { recursive: true });
      const uploadId = crypto.randomUUID();
      await fs.writeFile(path.join(uploadDir(dir), `${uploadId}.json`), JSON.stringify(meta));
      return {
        uploadId,
        url: `/api/storage/upload/${uploadId}`,
        method: "PUT",
        headers: { "Content-Type": meta.contentType },
        expiresAt: meta.expiresAt,
      };
    },

    async receiveUpload(uploadId, input) {
      const meta = await readPendingUploadMeta(dir, uploadId);
      if (Date.parse(meta.expiresAt) <= Date.now()) throw new Error("Upload grant expired");
      if ((input.contentType ?? "") !== meta.contentType) throw new Error("Upload content type mismatch");
      const buffer = Buffer.from(await input.body.arrayBuffer());
      await assertFileSize(buffer.length);
      await fs.mkdir(uploadDir(dir), { recursive: true });
      await fs.writeFile(path.join(uploadDir(dir), uploadId), buffer);
    },

    async finalizeUpload(input) {
      const pending = await readPendingUploadMeta(dir, input.uploadId);
      if (Date.parse(pending.expiresAt) <= Date.now()) throw new Error("Upload grant expired");
      const stagedPath = path.join(uploadDir(dir), input.uploadId);
      const buffer = await fs.readFile(stagedPath);
      if (buffer.length !== pending.declaredSize) throw new Error("Upload size mismatch");
      const actualChecksum = sha256(buffer);
      const expectedChecksum = normalizeChecksum(input.checksum) ?? pending.checksum;
      if (expectedChecksum && expectedChecksum !== actualChecksum)
        throw new Error("Upload checksum mismatch");
      await assertFileSize(buffer.length);
      await catalog?.assertCanStore({ size: buffer.length, quotaBytes: quota });
      await ensureStorageDir(dir);

      const id = crypto.randomUUID();
      const filePath = path.join(dir, id);
      await fs.rename(stagedPath, filePath);
      await fs.writeFile(
        `${filePath}.meta`,
        encodeMeta({
          name: pending.filename,
          type: pending.contentType,
          size: buffer.length,
          visibility: normalizeStorageVisibility(input.visibility),
        }),
      );
      await fs.unlink(path.join(uploadDir(dir), `${input.uploadId}.json`)).catch(() => {});
      const meta: LocalStorageFile = {
        id,
        name: pending.filename,
        size: buffer.length,
        type: pending.contentType,
        visibility: normalizeStorageVisibility(input.visibility),
        path: filePath,
      };
      metaStore.set(id, meta);
      try {
        await recordStored({ catalog, meta: toPublicStorageFile(meta), uploadedBy: "direct-upload" });
      } catch (error) {
        await removeStoredObjectFiles({ dir, filePath, storageId: id, metaStore });
        throw error;
      }
      return { assetId: id };
    },

    async abortUpload(uploadId) {
      await removePendingUpload(dir, uploadId);
    },

    async reconcileUploads(input) {
      const nowMs = input?.nowMs ?? Date.now();
      const maxAgeMs = input?.maxAgeMs ?? DIRECT_UPLOAD_TTL_MS;
      let removed = 0;
      const dirPath = uploadDir(dir);
      const entries = await fs.readdir(dirPath).catch(() => []);
      for (const entry of entries.filter((name) => name.endsWith(".json"))) {
        const uploadId = entry.replace(/\.json$/, "");
        const meta = await readPendingUploadMeta(dir, uploadId).catch(() => null);
        const expired =
          !meta || Date.parse(meta.expiresAt) <= nowMs || Date.parse(meta.expiresAt) + maxAgeMs <= nowMs;
        if (!expired) continue;
        await removePendingUpload(dir, uploadId);
        removed++;
      }
      return { removed };
    },

    async createReadGrant(storageId, input) {
      if (!options?.appName || !options?.readGrantSecret) {
        throw new Error("Storage read grants are not configured");
      }
      const meta = await loadStorageMeta({ storageId, dir, metaStore });
      if (!meta) throw new Error("Storage object not found");
      return createStorageReadGrant({
        appName: options.appName,
        storageId,
        secret: options.readGrantSecret,
        input,
        nowMs: options.readGrantNowMs?.(),
      });
    },

    async get(storageId: string): Promise<StoredFile | null> {
      const meta = await loadStorageMeta({ storageId, dir, metaStore });
      if (!meta) return null;
      const buffer = await fs.readFile(meta.path).catch(() => null);
      if (!buffer) return null;
      return {
        id: meta.id,
        name: meta.name,
        size: meta.size,
        type: meta.type,
        visibility: normalizeStorageVisibility(meta.visibility),
        buffer,
      };
    },

    async getMeta(storageId: string): Promise<StorageFile | null> {
      const meta = await loadStorageMeta({ storageId, dir, metaStore });
      if (meta) return toPublicStorageFile(meta);
      return (await catalog?.getMeta(storageId)) ?? null;
    },

    async setVisibility(storageId: string, visibility: StorageVisibility): Promise<void> {
      const meta = await loadStorageMeta({ storageId, dir, metaStore });
      if (!meta) throw new Error("Storage object not found");
      const nextVisibility = normalizeStorageVisibility(visibility);
      const nextMeta: LocalStorageFile = { ...meta, visibility: nextVisibility };
      const previousVisibility = normalizeStorageVisibility(meta.visibility);

      if (nextVisibility === "public" && catalog) {
        try {
          await catalog.setVisibility(storageId, nextVisibility);
        } catch {
          throw new Error("Storage catalog visibility update failed");
        }
        try {
          await fs.writeFile(`${meta.path}.meta`, encodeMeta(nextMeta));
          metaStore.set(storageId, nextMeta);
        } catch (error) {
          await catalog.setVisibility(storageId, previousVisibility).catch(() => {});
          throw error;
        }
        return;
      }

      await fs.writeFile(`${meta.path}.meta`, encodeMeta(nextMeta));
      metaStore.set(storageId, nextMeta);
      if (catalog) {
        try {
          await catalog.setVisibility(storageId, nextVisibility);
        } catch {
          throw new Error("Storage catalog visibility update failed");
        }
      }
    },

    async listMeta(input?: StorageListInput): Promise<StorageListPage> {
      if (!catalog) {
        throw new StorageListUnsupportedError(
          "listMeta is not supported on filesystem-only storage without a SQL catalog. " +
            "Configure rawSql to enable inventory listing.",
        );
      }
      return catalog.listMeta(input);
    },

    async delete(storageId: string): Promise<void> {
      const meta = metaStore.get(storageId);
      const filePath = meta?.path ?? path.join(dir, storageId);
      await fs.unlink(filePath).catch(() => {});
      await fs.unlink(`${filePath}.meta`).catch(() => {});
      metaStore.delete(storageId);

      await removeCachedTransforms(dir, storageId);
      if (catalog) await catalog.remove(storageId);
    },
  };
}

export function createRuntimeStorage(options: RuntimeStorageOptions): StorageRuntimeAdapter {
  const dir = options.storageDir ?? "./uploads";
  const adapterOptions = {
    ...options,
    storageQuota: options.storageQuotaBytes ?? options.storageQuota,
  };
  if (options.mode === "platform") {
    return createPlatformStorage(dir, adapterOptions);
  }
  return createLocalFilesystemStorage(dir, adapterOptions);
}

export function createPlatformStorage(
  dir: string = "./uploads",
  options?: StorageOptions,
): StorageRuntimeAdapter {
  const storage = createLocalFilesystemStorage(dir, options);
  return { ...storage, kind: "platform" };
}

export function createStorage(dir: string = "./uploads", options?: StorageOptions): StorageRuntimeAdapter {
  return createLocalFilesystemStorage(dir, options);
}
